/**
 * Cornerstone's `CORNERSTONE_VOLUME_NEW_IMAGE.imageIndex` reports the K-step
 * (the volume's position along the view-plane normal). The slider, scrollbar,
 * and `slotState.sliceIndex` all speak imageIds-list index. When the volume's
 * K-step direction runs opposite to imageIds order — set by the canonicalized
 * HTJ2K pipeline on some studies — the two differ by `N - 1 - i`, and feeding
 * K-step into React state where list-index is expected produces #401 (slider
 * snap-back, mirror-slice AI jump).
 *
 * This helper translates K-step → list-index by looking up the viewport's
 * current imageId in the imageIds array. The fallback path (currentImageId
 * not in list) is the bug condition the consumer must warn on; see
 * VolumeViewport.tsx `onVolumeNewImage`.
 */

export interface KStepLookup {
  /** Cornerstone's reported `imageIndex` on VOLUME_NEW_IMAGE. */
  kStepIndex: number;
  /** Frontend's authoritative imageIds for the series, list-index ordered. */
  imageIds: readonly string[];
  /** `viewport.getCurrentImageId()` — the imageId the viewport just landed on. */
  currentImageId: string | null | undefined;
}

export interface KStepResolution {
  listIndex: number;
  /** True when currentImageId resolved cleanly to a list position. False means
   *  the consumer is about to fall back to kStepIndex — the #401 path. */
  resolved: boolean;
}

export function kStepToListIndex({
  kStepIndex,
  imageIds,
  currentImageId,
}: KStepLookup): KStepResolution {
  if (!currentImageId) {
    return { listIndex: kStepIndex, resolved: false };
  }
  const idx = imageIds.indexOf(currentImageId);
  if (idx < 0) {
    return { listIndex: kStepIndex, resolved: false };
  }
  return { listIndex: idx, resolved: true };
}
